import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, Search, FileSignature, RefreshCw, CalendarDays, Plus, Database, Lock, Unlock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ContractEditor } from '../components/contract/ContractEditor';
import { templatesApi, bullhornApi, getErrorMessage } from '../services/api';
import {
  AUTOMATIC_FIELDS,
  FIELD_CUES,
  MANUAL_FIELD_TYPES,
  fieldCue,
  fieldKind,
  fieldTypeForBullhorn,
  mergeFieldIds,
  newFieldId,
  usedContractFields,
} from '../utils/contractFields';
import { textTemplateEntity, textTemplateRecipients, textTemplateRecord } from '../utils/textTemplateRecord';

/**
 * Text template editor: paste the text (or start from a converted Word file), select a
 * word such as LOCATION and pick the Bullhorn field it should become. The fields on offer
 * come from the kind of record the template is for (candidate, contact or placement).
 * Everything pasted is sanitised again on the server.
 */
export default function ContractTemplateEditorPage() {
  const { templateId } = useParams();
  const isNew = !templateId;
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();
  const editorRef = useRef(null);
  // A new template arrives from the Templates page with the answers from its dialog (and the
  // text, if a Word file was converted). Opened directly it is a placement template, as before.
  const draft = useLocation().state?.draft || null;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(draft?.name || '');
  const [html, setHtml] = useState(draft?.html || '');
  // null until a saved template has loaded: the record type is fixed when it is created.
  const [entityType, setEntityType] = useState(isNew ? textTemplateEntity(draft?.entity_type) : null);
  const record = textTemplateRecord(entityType);
  const [fieldsById, setFieldsById] = useState({});
  const [loaded, setLoaded] = useState(null); // the saved template, for settings we don't edit here
  const [flowBack, setFlowBack] = useState(true);
  // null = Bullhorn's default visibility (shown as "Public"); 'private' marks the uploaded file Private.
  const [fileVisibility, setFileVisibility] = useState(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [search, setSearch] = useState('');
  const [bullhornFields, setBullhornFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState(false);
  const [manualLabel, setManualLabel] = useState('');
  const [manualType, setManualType] = useState('text');

  // Fields of the template's record type from Bullhorn (live metadata).
  useEffect(() => {
    if (!entityType) return undefined;
    let cancelled = false;
    setFieldsLoading(true);
    setFieldsError(false);
    (async () => {
      try {
        const res = await bullhornApi.getEntityFields(entityType);
        const list = (res.data?.fields || []).map((f) => ({
          value: f.name,
          label: f.label || f.name,
          dataType: f.dataType,
          group: f.entity_group || entityType,
        }));
        if (!cancelled) setBullhornFields(list);
      } catch {
        if (!cancelled) setFieldsError(true);
      } finally {
        if (!cancelled) setFieldsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [entityType]);

  // Load an existing contract template.
  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await templatesApi.get(templateId);
        if (cancelled) return;
        if (data.source_format !== 'html') {
          navigate(getTenantPath(`/templates/${templateId}`), { replace: true });
          return;
        }
        setLoaded(data);
        setEntityType(textTemplateEntity(data.entity_type));
        setName(data.name || '');
        setHtml(data.html_content || '');
        setFlowBack(data.bullhorn_flow_back !== false);
        setFileVisibility(data.bullhorn_file_visibility || null);
        setRequiresApproval(!!data.requires_approval);
        const byId = {};
        for (const f of data.fields || []) {
          byId[f.id] = {
            id: f.id,
            kind: fieldKind(f),
            label: f.label,
            field_type: f.field_type || 'text',
            mapped_bullhorn_field: f.mapped_bullhorn_field || f.bullhorn_source_field || '',
            date_source: f.date_source || null,
          };
        }
        setFieldsById(byId);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Could not load this template'));
        navigate(getTenantPath('/templates'), { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId, isNew, navigate, getTenantPath]);

  const usedIds = useMemo(() => mergeFieldIds(html), [html]);
  // field id -> colour cue, for the chips in the text and the letters in the field list
  const fieldCues = useMemo(
    () => Object.fromEntries(Object.values(fieldsById).map((f) => [f.id, fieldCue(f)])),
    [fieldsById],
  );
  const usedFields = usedIds.map((id) => fieldsById[id]).filter(Boolean);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? bullhornFields.filter((f) => f.label.toLowerCase().includes(q) || f.value.toLowerCase().includes(q))
      : bullhornFields;
    return [...list].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })).slice(0, 80);
  }, [bullhornFields, search]);

  // Find the existing field that `match` accepts, or add `make()`'s result, then
  // put a chip for it in the text. Using the same field twice gives the same
  // value in both places.
  const placeField = (match, make) => {
    let field = Object.values(fieldsById).find(match);
    if (!field) {
      field = make();
      setFieldsById((prev) => ({ ...prev, [field.id]: field }));
    }
    editorRef.current?.insertField({ id: field.id, label: field.label, cue: fieldCue(field) });
  };

  // A field from Bullhorn: one template field per Bullhorn field.
  const chooseField = (bh) =>
    placeField(
      (f) => fieldKind(f) === 'bullhorn' && f.mapped_bullhorn_field === bh.value,
      () => ({
        id: newFieldId(),
        kind: 'bullhorn',
        label: bh.label,
        field_type: fieldTypeForBullhorn(bh.dataType),
        mapped_bullhorn_field: bh.value,
      }),
    );

  // An automatic date (today when sent, or the day it is signed).
  const chooseAutomatic = (auto) =>
    placeField(
      (f) => fieldKind(f) === 'auto' && f.date_source === auto.source,
      () => ({ id: newFieldId(), kind: 'auto', label: auto.label, field_type: 'date', date_source: auto.source }),
    );

  // A field the sender types in when sending; the same label is the same field.
  const addManualField = () => {
    const label = manualLabel.trim();
    if (!label) return;
    placeField(
      (f) => fieldKind(f) === 'manual' && f.label.toLowerCase() === label.toLowerCase() && f.field_type === manualType,
      () => ({ id: newFieldId(), kind: 'manual', label, field_type: manualType }),
    );
    setManualLabel('');
  };

  // Put another chip for a field that is already in the template.
  const insertAgain = (f) => editorRef.current?.insertField({ id: f.id, label: f.label, cue: fieldCue(f) });

  const KIND_LABEL = { bullhorn: record.label, auto: 'Automatic', manual: 'Manual' };

  const buildForm = () => {
    const form = new FormData();
    // A saved template keeps its own settings; a new one starts from the dialog's answers.
    const t = loaded || draft || {};
    const recipients = textTemplateRecipients(entityType);
    form.append('name', name.trim());
    form.append('description', t.description || '');
    form.append('entity_type', entityType);
    form.append('source_format', 'html');
    form.append('html_content', html || '');
    form.append('fields', JSON.stringify(usedContractFields(html, fieldsById)));
    form.append('template_mode', 'form');
    form.append('bullhorn_file_type', t.bullhorn_file_type || 'Signed Document');
    form.append('bullhorn_flow_back', flowBack ? 'true' : 'false');
    form.append('bullhorn_file_visibility', fileVisibility || '');
    form.append('requires_approval', requiresApproval ? 'true' : 'false');
    form.append('candidate_instructions', t.candidate_instructions || '');
    // The signer is the candidate (on a placement, the candidate on it) or, for a contact
    // template, the contact, and signs interactively.
    form.append('delivery_mode', 'interactive');
    form.append('send_to_candidate', String(recipients.send_to_candidate));
    form.append('send_to_contact', String(recipients.send_to_contact));
    form.append('candidate_delivery_mode', 'interactive');
    form.append('contact_delivery_mode', 'pdf_attachment');
    form.append('is_informational', 'false');
    form.append('credential_mapping_enabled', 'false');
    form.append('attachment_checklist', JSON.stringify(t.attachment_checklist || []));
    if (loaded) {
      // Keep settings this page doesn't edit exactly as they were saved.
      const tri = (v) => (v == null ? 'inherit' : v ? 'true' : 'false');
      form.set('notify_sender_on_completion', tri(t.notify_sender_on_completion));
      form.set('notify_recipient_on_completion', tri(t.notify_recipient_on_completion));
      form.set('send_completed_copy_to_recipient', t.send_completed_copy_to_recipient ? 'true' : 'false');
      form.set('reminder_days', t.reminder_days == null ? '' : String(t.reminder_days));
      form.set('estimated_minutes', t.estimated_minutes == null ? '' : String(t.estimated_minutes));
      form.set('notification_cc_emails', t.notification_cc_emails == null ? '' : JSON.stringify(t.notification_cc_emails));
    }
    return form;
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error('Give the template a name first');
      return;
    }
    if (!html.replace(/<[^>]*>/g, '').trim()) {
      toast.error('Paste the template text first');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await templatesApi.create(buildForm());
        toast.success('Text template created');
        navigate(getTenantPath(`/templates/contract/${data.id}`), { replace: true });
      } else {
        await templatesApi.update(templateId, buildForm());
        toast.success('Text template saved');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save the text template'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 text-muted-foreground flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="contract-template-editor">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate(getTenantPath('/templates'))}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Templates
          </Button>
          <FileSignature className="w-5 h-5 text-muted-foreground" />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template name"
            className="max-w-md"
            data-testid="contract-name-input"
          />
          <div className="flex-1" />
          {/* Same two switches as the PDF template editor's top bar */}
          <Button
            variant={flowBack ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFlowBack(!flowBack)}
            className={`gap-2 ${flowBack ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
            data-testid="contract-flow-back-toggle"
            title={flowBack ? 'Completed documents upload to the Bullhorn Files tab' : 'Completed documents stay in HyreDocs only — nothing is written to Bullhorn Files'}
          >
            <Database className="w-4 h-4" />
            {flowBack ? 'Flow back: On' : 'Flow back: Off'}
          </Button>
          <Button
            variant={fileVisibility === 'private' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFileVisibility(fileVisibility === 'private' ? null : 'private')}
            className={`gap-2 ${fileVisibility === 'private' ? 'bg-purple-500 hover:bg-purple-600' : ''}`}
            disabled={!flowBack}
            data-testid="contract-visibility-toggle"
            title={!flowBack
              ? 'Enable Flow back to control the uploaded file’s Bullhorn visibility'
              : fileVisibility === 'private' ? 'Uploaded file is marked Private in Bullhorn' : 'Uploaded file uses Bullhorn’s default visibility (Public)'}
          >
            {fileVisibility === 'private' ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            {fileVisibility === 'private' ? 'Private' : 'Public'}
          </Button>
          <Button onClick={save} disabled={saving} className="btn-primary" data-testid="contract-save-button">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>

        {/* On a wide screen this area has a fixed height and its two columns scroll on
            their own: the field panel and the editor toolbar stay in view while the
            document text scrolls. (A sticky sidebar would not work here: the dashboard's
            main area is itself a scroll container.) On a narrow screen it simply flows. */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 items-start lg:items-stretch lg:h-[calc(100vh-13.25rem)] lg:min-h-[480px]">
          <div className="min-w-0 lg:h-full lg:min-h-0">
            <ContractEditor
              ref={editorRef}
              html={html}
              onChange={setHtml}
              onSelectionChange={setSelectedText}
              fieldCues={fieldCues}
              signer={entityType === 'contact' ? 'contact' : 'candidate'}
            />
          </div>

          <aside className="space-y-4 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:pr-1" data-testid="contract-sidebar">
            <div className="border rounded-lg bg-white p-4 space-y-3" data-testid="contract-field-panel">
              <div>
                <h3 className="font-heading text-sm font-semibold">Insert a field</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedText
                    ? <>Selected: <span className="font-medium text-foreground">“{selectedText.length > 40 ? `${selectedText.slice(0, 40)}…` : selectedText}”</span> — choose a field to replace it.</>
                    : 'Select the text to replace (for example LOCATION), then choose a field. With nothing selected the field is added at the cursor.'}
                </p>
              </div>

              <p className="text-[11px] uppercase tracking-wide font-medium text-muted-foreground">{record.fieldsLabel}</p>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={record.searchPlaceholder}
                  className="pl-8"
                  data-testid="contract-field-search"
                />
              </div>
              <div className="max-h-80 overflow-y-auto -mx-1">
                {fieldsLoading && <p className="px-1 py-2 text-xs text-muted-foreground">Loading fields from Bullhorn…</p>}
                {fieldsError && (
                  <p className="px-1 py-2 text-xs text-red-600">
                    Couldn’t load fields from Bullhorn. Check the Bullhorn connection in Settings.
                  </p>
                )}
                {!fieldsLoading && !fieldsError && filtered.length === 0 && (
                  <p className="px-1 py-2 text-xs text-muted-foreground">No fields match.</p>
                )}
                {filtered.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault() /* keep the text selection */}
                    onClick={() => chooseField(f)}
                    title={f.label}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 flex items-center justify-between gap-2"
                    data-testid={`contract-field-${f.value}`}
                  >
                    <span className="text-sm truncate">{f.label}</span>
                    <code className="text-[10px] text-muted-foreground shrink-0">{f.value}</code>
                  </button>
                ))}
              </div>

              {/* A field with no Bullhorn source: the sender fills it in when sending */}
              <div className="space-y-1.5" data-testid="contract-manual-fields">
                <p className="text-[11px] uppercase tracking-wide font-medium text-muted-foreground">Entered when sending</p>
                <div className="flex gap-1.5">
                  <Input
                    value={manualLabel}
                    onChange={(e) => setManualLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManualField(); } }}
                    placeholder="Field name, e.g. Daily rate"
                    data-testid="contract-manual-label"
                  />
                  <select
                    value={manualType}
                    onChange={(e) => setManualType(e.target.value)}
                    className="border rounded-md px-2 text-sm bg-white"
                    aria-label="Field type"
                    data-testid="contract-manual-type"
                  >
                    {MANUAL_FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={!manualLabel.trim()}
                    onMouseDown={(e) => e.preventDefault() /* keep the text selection */}
                    onClick={addManualField}
                    title="Add field"
                    data-testid="contract-manual-add"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Whoever sends the document types this in first — it can’t be sent while it’s blank.
                </p>
              </div>

              {/* Automatic dates — no Bullhorn field and nothing to type */}
              <div className="space-y-1.5" data-testid="contract-automatic-fields">
                <p className="text-[11px] uppercase tracking-wide font-medium text-muted-foreground">Automatic</p>
                {AUTOMATIC_FIELDS.map((a) => (
                  <button
                    key={a.source}
                    type="button"
                    onMouseDown={(e) => e.preventDefault() /* keep the text selection */}
                    onClick={() => chooseAutomatic(a)}
                    className="w-full text-left px-2 py-1.5 rounded border hover:bg-slate-50 flex items-center gap-2"
                    data-testid={`contract-auto-${a.source}`}
                  >
                    <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm">{a.label}</span>
                    <span className="text-[11px] text-muted-foreground truncate">{a.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border rounded-lg bg-white p-4 space-y-2" data-testid="contract-used-fields">
              <h3 className="font-heading text-sm font-semibold">Fields in this template (click to insert again)</h3>
              {usedFields.length === 0 ? (
                <p className="text-xs text-muted-foreground">None yet.</p>
              ) : (
                <ul className="space-y-1">
                  {usedFields.map((f) => (
                    <li key={f.id} className="text-sm">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertAgain(f)}
                        title={f.label}
                        className="w-full flex items-center justify-between gap-2 text-left rounded px-1 py-0.5 hover:bg-slate-50"
                        data-testid={`contract-used-${f.id}`}
                      >
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span
                            className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded text-[10px] font-bold shrink-0 ${FIELD_CUES[fieldCue(f)].badge}`}
                            title={FIELD_CUES[fieldCue(f)].label}
                            data-testid={`contract-used-cue-${f.id}`}
                          >
                            {FIELD_CUES[fieldCue(f)].letter}
                          </span>
                          <span className="truncate">{f.label}</span>
                        </span>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {fieldKind(f) === 'bullhorn' ? f.mapped_bullhorn_field : KIND_LABEL[fieldKind(f)]}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {/* What each letter and colour means */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1" data-testid="contract-cue-legend">
                {Object.entries(FIELD_CUES).map(([key, cue]) => (
                  <span key={key} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    <span className={`inline-flex items-center justify-center w-[14px] h-[14px] rounded text-[9px] font-bold ${cue.badge}`}>{cue.letter}</span>
                    {cue.label}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">
                Every field is required: a document can’t be sent until each one has a value.
              </p>
            </div>

            <div className="border rounded-lg bg-white p-4 space-y-3">
              <h3 className="font-heading text-sm font-semibold">Settings</h3>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="contract-approval" className="text-sm">Require approval after signing</Label>
                <Switch id="contract-approval" checked={requiresApproval} onCheckedChange={setRequiresApproval} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}
